/**
 * 海报组件
 * 基于 Canvas 生成海报图片，支持图片、文字、线条、矩形、圆形、二维码等元素
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { PosterProps, PosterConfig, ExportOptions } from './types';
import {
  drawElement,
  loadImage,
  applyGradient,
} from './utils';

export function Poster({
  config,
  onSuccess,
  onError,
  className,
  style,
}: PosterProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 绘制海报
   */
  const drawPoster = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const {
        width,
        height,
        backgroundColor = '#ffffff',
        backgroundImage,
        gradient,
        views,
        debug = false,
        pixelRatio = window.devicePixelRatio || 1,
      } = config;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      // 设置 canvas 尺寸（考虑像素比）
      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;
      // CSS 尺寸用于显示，保持宽高比
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
      canvas.style.maxWidth = `${width}px`;

      // 缩放上下文以适应像素比
      ctx.scale(pixelRatio, pixelRatio);

      // 绘制背景
      if (gradient) {
        const grad = applyGradient(ctx, gradient, 0, 0, width, height);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      } else if (backgroundImage) {
        try {
          const img = await loadImage(backgroundImage);
          ctx.drawImage(img, 0, 0, width, height);
        } catch (err) {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, width, height);
        }
      } else {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
      }

      // 绘制所有元素
      for (const element of views) {
        try {
          await drawElement(ctx, element);
        } catch (err) {
          if (debug) {
            // 调试模式下绘制错误标记
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 2;
            ctx.strokeRect(0, 0, width, height);
            ctx.fillStyle = '#ff0000';
            ctx.font = '12px sans-serif';
            ctx.fillText(
              `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
              10,
              20
            );
          }
        }
      }

      // 调试模式下显示网格
      if (debug) {
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.lineWidth = 1;
        for (let i = 0; i <= width; i += 50) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, height);
          ctx.stroke();
        }
        for (let i = 0; i <= height; i += 50) {
          ctx.beginPath();
          ctx.moveTo(0, i);
          ctx.lineTo(width, i);
          ctx.stroke();
        }
      }

      // 生成 base64
      const base64 = canvas.toDataURL('image/png');
      onSuccess?.(base64);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      onError?.(error);
    } finally {
      setIsGenerating(false);
    }
  }, [config, onSuccess, onError]);

  // 当配置变化时重新绘制
  useEffect(() => {
    drawPoster();
  }, [drawPoster]);

  return (
    <div className={className} style={style}>
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          height: 'auto',
        }}
      />
      {error && (
        <div
          style={{
            padding: '8px',
            background: '#fee',
            color: '#c00',
            fontSize: '12px',
            marginTop: '8px',
          }}
        >
          错误: {error.message}
        </div>
      )}
      {isGenerating && (
        <div
          style={{
            padding: '8px',
            background: '#eef',
            color: '#006',
            fontSize: '12px',
            marginTop: '8px',
          }}
        >
          生成中...
        </div>
      )}
    </div>
  );
}

/**
 * 带 ref 的 Poster 组件，支持导出方法
 */
export interface PosterRef {
  exportImage: (options?: ExportOptions) => void;
  regenerate: () => Promise<void>;
}

export const PosterWithRef = React.forwardRef<PosterRef, PosterProps>(
  (props, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const drawPoster = React.useCallback(async () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      setIsGenerating(true);
      setError(null);

      try {
        const {
          width,
          height,
          backgroundColor = '#ffffff',
          backgroundImage,
          gradient,
          views,
          debug = false,
          pixelRatio = window.devicePixelRatio || 1,
        } = props.config;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        canvas.width = width * pixelRatio;
        canvas.height = height * pixelRatio;
        // CSS 尺寸用于显示，保持宽高比
        canvas.style.width = '100%';
        canvas.style.height = 'auto';
        canvas.style.maxWidth = `${width}px`;

        ctx.scale(pixelRatio, pixelRatio);

        if (gradient) {
          const grad = applyGradient(ctx, gradient, 0, 0, width, height);
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, width, height);
        } else if (backgroundImage) {
          try {
            const img = await loadImage(backgroundImage);
            ctx.drawImage(img, 0, 0, width, height);
          } catch (err) {
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, width, height);
          }
        } else {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, width, height);
        }

        for (const element of views) {
          try {
            await drawElement(ctx, element);
          } catch (err) {
            if (debug) {
              ctx.strokeStyle = '#ff0000';
              ctx.lineWidth = 2;
              ctx.strokeRect(0, 0, width, height);
              ctx.fillStyle = '#ff0000';
              ctx.font = '12px sans-serif';
              ctx.fillText(
                `Error: ${err instanceof Error ? err.message : 'Unknown error'}`,
                10,
                20
              );
            }
          }
        }

        if (debug) {
          ctx.strokeStyle = 'rgba(255, 0, 0, 0.3)';
          ctx.lineWidth = 1;
          for (let i = 0; i <= width; i += 50) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, height);
            ctx.stroke();
          }
          for (let i = 0; i <= height; i += 50) {
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(width, i);
            ctx.stroke();
          }
        }

        const base64 = canvas.toDataURL('image/png');
        props.onSuccess?.(base64);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        props.onError?.(error);
      } finally {
        setIsGenerating(false);
      }
    }, [props.config]);

    useEffect(() => {
      drawPoster();
    }, [drawPoster]);

    const exportImage = React.useCallback((options: ExportOptions = {}) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas not available');
      }

      const {
        format = 'png',
        quality = props.config.quality ?? 0.92,
        filename = 'poster',
      } = options;

      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const dataUrl = canvas.toDataURL(mimeType, quality);

      const link = document.createElement('a');
      link.download = `${filename}.${format}`;
      link.href = dataUrl;
      link.click();
    }, [props.config.quality]);

    React.useImperativeHandle(ref, () => ({
      exportImage,
      regenerate: drawPoster,
    }), [exportImage, drawPoster]);

    return (
      <div className={props.className} style={props.style}>
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
          }}
        />
        {error && (
          <div
            style={{
              padding: '8px',
              background: '#fee',
              color: '#c00',
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            错误: {error.message}
          </div>
        )}
        {isGenerating && (
          <div
            style={{
              padding: '8px',
              background: '#eef',
              color: '#006',
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            生成中...
          </div>
        )}
      </div>
    );
  }
);

PosterWithRef.displayName = 'PosterWithRef';
